<template>
    <card :title="`PO# ${this.order.poNumber}`">
        <b-row>
            <b-col>

                <b-row>
                    <b-col cols="12">
                        <h5>Order: {{ ediOrderStatus[order.status] }}</h5>
                    </b-col>
                    <b-col>
                        <strong>Created:</strong>
                        {{ dateFormatter(order.created) }}<br />
                        <strong>Created By:</strong> {{ order.createdBy }}<br />
                        <strong>Updated:</strong>
                        {{ dateFormatter(order.updated) }}<br />
                        <strong>Updated By:</strong> {{ order.updatedBy }}
                    </b-col>
                </b-row>
            </b-col>
            <b-col>
                <b-row>
                    <b-col cols="12">
                        <h5>Store: {{ this.store.name }}</h5>
                    </b-col>
                    <b-col cols="2">
                        <strong>Ship To:</strong>
                    </b-col>
                    <b-col>
                        {{ this.store.address1 }}<br />
                        <div v-if="this.store.address2?.length > 0">
                            {{ this.store.address2 }}<br />
                        </div>
                        {{ this.store.addressCity }}, {{ this.store.addressState }} {{ this.store.addressZip }}
                    </b-col>
                </b-row>
            </b-col>
            <b-col>
                <b-row>
                    <b-col>
                        <h5>Vendor: {{ this.vendor.name }}</h5>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-button v-if="order.status == 0"
                    variant="primary"
                    v-b-modal.drug-modal>
                    <i class="fas fa-plus" /> Add Item...
                </b-button>
                <b-button v-if="order.status == 0"
                    variant="success"
                    @click="submitOrder">
                    <i class="fas fa-cash-register" /> Submit Order
                </b-button>
                <b-button v-if="order.status == 0"
                    variant="danger"
                    @click="cancelOrder">
                    <i class="fas fa-window-close" /> Cancel Order
                </b-button>
                <b-button v-if="order.status == 1 || order.status == 2"
                    variant="primary"
                    @click="receiveOrder">
                    <i class="fas fa-arrow-to-bottom" /> Receive Order
                </b-button>
                <b-button v-if="order.status == 3"
                    variant="primary"
                    @click="markInvoiced">
                    <i class="fas fa-file-invoice-dollar" /> Mark Invoiced
                </b-button>
                <b-button v-if="order.status == 4"
                    variant="primary">
                    <i class="fas fa-eye" /> View Invoice
                </b-button>
            </b-col>
        </b-row>

        <hr />

        <b-table
            striped
            foot-clone
            no-footer-sorting
            :items="order.lineItems"
            :fields="fields"
            :busy="isLoading"
            show-empty
            empty-text="No Items Found">
            <template #table-busy>
                <div class="text-center my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Loading...</strong>
                </div>
            </template>
            <template #foot(extendedPrice)>
                {{ moneyFormatter(totalExtendedPrice) }}
            </template>
            <template #foot(ndC11)> Total:</template>
            <template #foot(drugName)>&nbsp;</template>
            <template #foot(unitPrice)>&nbsp;</template>
            <template #foot(packagesOrdered)>
                {{ totalPackagesOrdered }}
            </template>
            <template #foot(packagesReceived)>
                &nbsp;
                <span v-if="order.status >= 3">{{ totalPackagesReceived }}</span>
            </template>
            <template #foot(packagesInvoiced)>
                &nbsp;
                <span v-if="order.status == 4">{{ totalPackagesInvoiced }}</span>
            </template>
            <template #foot(invoicedPrice)>
                &nbsp;
                <span v-if="order.status == 4">{{ moneyFormatter(totalInvoicedPrice) }}</span>
            </template>
        </b-table>

        <b-row>
            <b-col>
                <router-link :to="{name:'EDIOrdering'}">
                    <b-button>Back To Queue</b-button>
                </router-link>
            </b-col>
        </b-row>

        <b-modal id="drug-modal" title="Add Item" @ok="addItem" @hidden="clearModal">
            <SearchComponent
                id="drugSearch"
                v-model="orderableDrug"
                label="Drug"
                :search-u-r-l="`/EDIOrdering/OrderableDrugs/${this.order.vendorId}`"
                required
                clearable
                prepopulate />
            <b-col cols="6">
                <b-form-group label="Quantity">
                    <b-input type="number" v-model.number="quantityToOrder" />
                </b-form-group>
            </b-col>
        </b-modal>

    </card>
</template>

<script lang="ts">
import SearchComponent from "@/components/SearchComponent.vue";
import { Component, Vue } from "vue-property-decorator";
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import EnumSelector from "@/components/EnumSelector.vue";
import moment from "moment";
import numeral from "numeral";
import { EDIOrder, EDIOrderStatus, OrderableDrug, EDIOrderLineItem } from "@/models/EDI/EDIOrder";
import { Store } from "@/models/Store";
import { Vendor } from "@/models/Vendor";

@Component({
    name: "EDIOrderDetails",
    components: {
        EnumSelector,
        SearchComponent,
    },
})
export default class EDIOrderDetails extends Vue {
    private isLoading = false;
    protected ediOrderStatus = EDIOrderStatus;
    private orderableDrug: OrderableDrug = new OrderableDrug();
    private quantityToOrder: number | null = null;

    private order: EDIOrder = new EDIOrder();

    protected selectedStatus: string | null = null;
    private fields: any[] = [
        {
            key: "ndC11",
            label: "NDC",
            sortable: true,
        },
        {
            key: "drugName",
            label: "Drug Name",
            sortable: true,
        },
        {
            key: "unitPrice",
            label: "Unit Price",
            formatter: this.moneyFormatter,
            sortable: true,
        },
        {
            key: "packagesOrdered",
            label: "Packages Ordered",
            sortable: true,
        },
        {
            key: "extendedPrice",
            label: "Extended Price",
            formatter: this.moneyFormatter,
            sortable: true,
        },
        {
            key: "packagesReceived",
            label: "Packages Received",
            sortable: true,
        },
        {
            key: "packagesInvoiced",
            label: "Packages Invoiced",
            sortable: true,
        },
        {
            key: "invoicedPrice",
            label: "Invoiced Price",
            formatter: this.moneyFormatter,
            sortable: true,
        },
    ];
    private store: Store = new Store();
    private vendor: Vendor = new Vendor();

    get statusName() {
        return EDIOrderStatus[this.order.status];
    }

    moneyFormatter(value: number) {
        if (value == null) return "";
        return numeral(value).format("$0,0.00");
    }

    dateFormatter(value: Date): string {
        const date = moment.utc(value).local();
        return date.format("MM-DD-YYYY hh:mm a");
    }

    created() {
        this.loadData();
    }

    loadData() {
        this.isLoading = true;
        axios
            .get<EDIOrder>(`EDIOrdering/${this.$route.params.id}`)
            .then((res) => {
                this.order = new EDIOrder(res.data);
                axios
                    .get<Store>(`Store/${this.order.storeId}`)
                    .then((res) => {
                        this.store = new Store(res.data);
                    })
                    .catch((err) => {
                        this.$notification(NotificationOptions.error(err));
                    });
                axios
                    .get<Vendor>(`Vendor/${this.order.vendorId}`)
                    .then((res) => {
                        this.vendor = new Vendor(res.data);
                    })
                    .catch((err) => {
                        this.$notification(NotificationOptions.error(err));
                    });
            })
            .catch((err) => {
                this.$notification(NotificationOptions.error(err));
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    get totalExtendedPrice() {
        return this.order?.lineItems.reduce((pv, nv) => pv + nv.extendedPrice, 0);
    }

    get totalPackagesOrdered() {
        return this.order?.lineItems.reduce((pv, nv) => pv + nv.packagesOrdered, 0);
    }

    get totalPackagesReceived() {
        return this.order?.lineItems.reduce((pv, nv) => pv + (nv.packagesReceived ?? 0), 0);
    }

    get totalPackagesInvoiced() {
        return this.order?.lineItems.reduce((pv, nv) => pv + (nv.packagesInvoiced ?? 0), 0);
    }

    get totalInvoicedPrice() {
        return this.order?.lineItems.reduce((pv, nv) => pv + (nv.invoicedPrice ?? 0), 0);
    }

    async submitOrder() {
        try {
            await axios.post<EDIOrder>(`/EdiOrdering/Submit`, this.order);
            // this.order = new EDIOrder(resp.data);
            this.loadData();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async cancelOrder() {
        try {
            await axios.post(`/EdiOrdering/Cancel/${this.order.id}`);
            this.loadData();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async receiveOrder() {
        try {
            await axios.post<EDIOrder>(`/EdiOrdering/Receive`, this.order);
            // this.order = new EDIOrder(resp.data);
            this.loadData();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async markInvoiced() {
        try {
            await axios.post(`/EdiOrdering/Invoiced`, this.order);
            this.loadData();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    addItem() {
        const item = EDIOrderLineItem.fromOrderableDrug(this.orderableDrug, this.quantityToOrder);
        this.order.lineItems.push(item);
    }

    clearModal() {
        this.orderableDrug = new OrderableDrug();
        this.quantityToOrder = 0;
    }

}
</script>


<style scoped>
</style>
